import { ConnectionLost } from "@/components/common/ConnectionLost";
import { CountUp } from "@/components/common/CountUp";
import { ProgressRing } from "@/components/course/ProgressRing";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useEnrollments } from "@/hooks/useEnrollments";
import { useMe } from "@/hooks/useMe";
import { getCourses } from "@/lib/api";
import type { Course, Enrollment } from "@/lib/api-types";
import { NetworkError } from "@/lib/api-types";
import { queryKeys } from "@/lib/query-keys";
import { cn, formatDuration } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import {
	AlertTriangle,
	ArrowRight,
	BookOpen,
	CheckCircle,
	ChevronRight,
	Clock,
	Play,
	Trophy,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

function getTimeOfDay(): string {
	const hour = new Date().getHours();
	if (hour < 12) return "morning";
	if (hour < 18) return "afternoon";
	return "evening";
}

export function Dashboard() {
	const { t } = useTranslation();
	const { data: meData } = useMe();
	const user = meData?.user;
	const {
		data: allEnrollments,
		isLoading: enrollmentsLoading,
		isError: enrollmentsError,
		error: enrollmentsErrorObj,
	} = useEnrollments();
	const { data: coursesData } = useQuery({
		queryKey: queryKeys.courses.all,
		queryFn: () => getCourses(),
	});

	const enrollments = allEnrollments?.filter((e) => e.status === "active");
	const hasEnrollments = enrollments && enrollments.length > 0;
	const courses = coursesData?.courses ?? [];

	const totalModulesCompleted = enrollments?.reduce((sum, e) => sum + e.modules_completed, 0) ?? 0;

	const lastActive = enrollments?.[0];
	const firstName = user?.name?.split(" ")[0] ?? "";
	const greetingPrefix =
		t("dashboard.greeting", {
			timeOfDay: getTimeOfDay(),
			name: "__NAME__",
		}).split("__NAME__")[0] ?? "";
	const greetingWords = greetingPrefix.trim().split(" ");

	const subtitle = lastActive
		? t("dashboard.subtitle", {
				percent: Math.round(lastActive.progress_percent),
				course: lastActive.course_title,
			})
		: t("dashboard.subtitleNoCourse");

	return (
		<div className="mx-auto max-w-6xl px-6 py-8">
			{/* Welcome */}
			<div className="mb-8">
				<h1 className="mb-1 text-3xl font-bold text-primary">
					{greetingWords.map((word, i) => (
						<span
							key={`${word}-${i}`}
							className="inline-block animate-blur-in opacity-0"
							style={{
								animationDelay: `${i * 80}ms`,
								animationFillMode: "forwards",
							}}
						>
							{word}&nbsp;
						</span>
					))}
					<span
						className="inline-block animate-blur-in gradient-text opacity-0"
						style={{
							animationDelay: `${greetingWords.length * 80}ms`,
							animationFillMode: "forwards",
						}}
					>
						{firstName}
					</span>
				</h1>
				{hasEnrollments && (
					<p
						className="animate-blur-in text-base text-muted opacity-0"
						style={{ animationDelay: "400ms", animationFillMode: "forwards" }}
					>
						{subtitle}
					</p>
				)}
			</div>

			{enrollmentsLoading ? (
				<DashboardSkeleton />
			) : enrollmentsError && enrollmentsErrorObj instanceof NetworkError ? (
				<ConnectionLost />
			) : enrollmentsError ? (
				<div
					className="flex flex-col items-center gap-4 glass-card rounded-2xl p-12 text-center"
					role="alert"
				>
					<AlertTriangle className="h-10 w-10 text-error" aria-hidden="true" />
					<h2 className="text-lg font-semibold text-primary">{t("error.loadFailed")}</h2>
					<p className="text-sm text-secondary">{t("error.loadFailedDesc")}</p>
				</div>
			) : hasEnrollments ? (
				<>
					{/* Stats strip */}
					<div className="stagger-list mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
						<StatCard
							label={t("dashboard.stats.courses")}
							icon={<BookOpen className="h-4 w-4 text-accent/60" aria-hidden="true" />}
							value={enrollments.length}
						/>
						<StatCard
							label={t("dashboard.stats.modulesDone")}
							icon={<CheckCircle className="h-4 w-4 text-success/60" aria-hidden="true" />}
							value={totalModulesCompleted}
						/>
						<StatCard
							label={t("dashboard.stats.watchTime")}
							icon={<Clock className="h-4 w-4 text-info/60" aria-hidden="true" />}
							displayValue={"\u2014"}
						/>
						<StatCard
							label={t("dashboard.stats.quizzesPassed")}
							icon={<Trophy className="h-4 w-4 text-warning/60" aria-hidden="true" />}
							displayValue={"\u2014"}
						/>
					</div>

					{/* Quick resume */}
					{lastActive && <ResumeCard enrollment={lastActive} />}

					{/* My Courses */}
					<div className="mb-5 flex items-center justify-between">
						<h2 className="text-lg font-semibold text-primary/90">{t("dashboard.myCourses")}</h2>
						<Link
							to="/courses"
							className="text-sm text-accent transition-colors hover:text-accent-hover"
						>
							{t("dashboard.browseCatalog")}
						</Link>
					</div>
					<div className="stagger-list grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
						{enrollments.map((enrollment) => {
							const course = courses.find((c) => c.id === enrollment.course_id);
							return (
								<DashboardCourseCard key={enrollment.id} enrollment={enrollment} course={course} />
							);
						})}
					</div>

					{/* Deferred: Recent Activity section */}
					{/* Deferred: Milestones / Achievements section */}
				</>
			) : (
				/* Empty state */
				<div className="text-center">
					<div className="mx-auto max-w-md glass-card rounded-2xl p-8 sm:p-12">
						<BookOpen className="mx-auto mb-4 h-12 w-12 text-accent" aria-hidden="true" />
						<h2 className="mb-2 text-xl font-semibold text-primary">{t("dashboard.empty")}</h2>
						<p className="mb-6 text-secondary">{t("dashboard.emptyDesc")}</p>
						<Button asChild>
							<Link to="/courses">
								{t("dashboard.browseCourses")}
								<ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
							</Link>
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}

/* ---------- Internal components ---------- */

function StatCard({
	label,
	icon,
	value,
	displayValue,
}: {
	label: string;
	icon: React.ReactNode;
	value?: number;
	displayValue?: string;
}) {
	return (
		<div className="glass-card rounded-xl p-5">
			<div className="mb-3 flex items-start justify-between">
				<span className="text-xs font-medium uppercase tracking-wider text-muted">{label}</span>
				{icon}
			</div>
			<p className="text-3xl font-bold text-primary">
				{displayValue ?? (value !== undefined ? <CountUp target={value} /> : "\u2014")}
			</p>
		</div>
	);
}

function ResumeCard({ enrollment }: { enrollment: Enrollment }) {
	const { t } = useTranslation();

	return (
		<Link to={`/learn/${enrollment.course_id}`} className="group mb-8 block">
			<div className="glass-card rounded-2xl border-accent/15 bg-gradient-to-r from-accent/5 to-transparent p-6">
				{/* Header label */}
				<div className="mb-4 flex items-center gap-2">
					<Play className="h-4 w-4 text-accent" fill="currentColor" aria-hidden="true" />
					<span className="text-xs font-semibold uppercase tracking-wider text-accent">
						{t("dashboard.resume.label")}
					</span>
				</div>

				{/* Content row */}
				<div className="flex flex-col gap-5 md:flex-row md:items-center">
					{/* Thumbnail placeholder */}
					<div className="flex h-20 w-full shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-accent/20 to-bg-overlay md:w-32">
						<span className="text-2xl font-bold text-accent/60">
							{enrollment.course_title.slice(0, 2).toUpperCase()}
						</span>
					</div>

					{/* Info */}
					<div className="min-w-0 flex-1">
						<p className="mb-1 text-xs text-muted">
							{t("dashboard.courseCard.modulesOf", {
								completed: enrollment.modules_completed,
								total: enrollment.modules_total,
							})}
						</p>
						<h3 className="mb-2 truncate text-lg font-semibold text-primary">
							{enrollment.course_title}
						</h3>
						<div className="flex items-center gap-3">
							<div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
								<div
									className="h-full rounded-full bg-accent transition-all duration-500 ease-out motion-reduce:transition-none"
									style={{ width: `${enrollment.progress_percent}%` }}
								/>
							</div>
							<span className="shrink-0 text-xs text-muted">
								{enrollment.modules_completed}/{enrollment.modules_total}
							</span>
						</div>
					</div>

					{/* Resume button (span, not button — nested inside Link) */}
					<span className="flex shrink-0 items-center gap-2 rounded-xl bg-accent px-6 py-3 text-sm font-semibold text-white shadow-glow-sm transition-colors group-hover:bg-accent-hover">
						<Play className="h-4 w-4" fill="currentColor" aria-hidden="true" />
						{t("dashboard.resume.button")}
					</span>
				</div>
			</div>
		</Link>
	);
}

function DashboardCourseCard({
	enrollment,
	course,
}: {
	enrollment: Enrollment;
	course?: Course;
}) {
	const { t } = useTranslation();
	const isCompleted = enrollment.progress_percent >= 100;
	const language = course?.language ?? "en";
	const description = course?.description ?? "";
	const totalDurationSec = course?.total_duration_sec ?? 0;

	const remainingSec =
		totalDurationSec > 0
			? Math.round(totalDurationSec * (1 - enrollment.progress_percent / 100))
			: 0;

	return (
		<Link
			to={`/learn/${enrollment.course_id}`}
			className="group flex flex-col glass-card rounded-2xl p-6"
		>
			{/* Top badges — language left, status right */}
			<div className="mb-4 flex items-center justify-between">
				<span className="rounded bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white/60">
					{language.toUpperCase()}
				</span>
				{isCompleted ? (
					<span className="rounded border border-success/20 bg-success/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-success">
						{t("dashboard.courseStatus.completed")}
					</span>
				) : (
					<span className="rounded border border-accent/20 bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
						{t("dashboard.courseStatus.inProgress")}
					</span>
				)}
			</div>

			{/* Title + description */}
			<h3 className="mb-1 text-base font-semibold text-primary transition-colors group-hover:text-accent">
				{enrollment.course_title}
			</h3>
			{description && <p className="mb-5 text-sm text-muted line-clamp-2">{description}</p>}

			{/* Progress ring + meta */}
			<div className="mt-auto flex items-center gap-4">
				<ProgressRing
					progress={enrollment.progress_percent}
					size="lg"
					showCompletedIcon={isCompleted}
				/>
				<div className="min-w-0 flex-1">
					<p className="text-sm font-medium text-primary/80">
						{t("dashboard.courseCard.modulesOf", {
							completed: enrollment.modules_completed,
							total: enrollment.modules_total,
						})}
					</p>
					<p className="mt-0.5 text-xs text-muted">
						{isCompleted
							? t("dashboard.courseStatus.completed")
							: remainingSec > 0
								? t("dashboard.courseCard.timeRemaining", {
										time: formatDuration(remainingSec),
									})
								: null}
					</p>
				</div>
			</div>

			{/* Footer with separator */}
			<div className="mt-5 border-t border-white/5 pt-4">
				<span
					className={cn(
						"flex items-center gap-1 text-sm font-medium transition-all group-hover:gap-2",
						isCompleted ? "text-muted group-hover:text-secondary" : "text-accent",
					)}
				>
					{isCompleted
						? t("dashboard.courseCard.reviewCourse")
						: t("dashboard.courseCard.continueLearning")}
					<ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
				</span>
			</div>
		</Link>
	);
}

function DashboardSkeleton() {
	return (
		<div className="space-y-6" aria-busy="true">
			<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
				{Array.from({ length: 4 }).map((_, i) => (
					<Skeleton key={`stat-${i}`} className="h-24 rounded-xl" />
				))}
			</div>
			<Skeleton className="h-40 rounded-2xl" />
			<div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
				{Array.from({ length: 3 }).map((_, i) => (
					<Skeleton key={`card-${i}`} className="h-72 rounded-2xl" />
				))}
			</div>
		</div>
	);
}
